/**
 * WebCropOverlay — interactive crop UI for web.
 *
 * Pure React (DOM) component rendered imperatively via react-dom/client.
 * Only imported/used when Platform.OS === 'web' (in picker-utils.ts).
 *
 * UX:
 *  - Image is displayed at contained size (max 560 px on the longest edge).
 *  - A draggable crop rectangle sits on top.
 *  - Four corner handles resize the rectangle.
 *  - Cancel: resolves with null (caller treats as cancellation).
 *  - Crop: resolves with the crop region in original-image pixel coordinates.
 *
 * FIX-03 / AC-F3-03, AC-F3-04, AC-F3-05
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';

export type CropRegion = {
  originX: number;
  originY: number;
  width: number;
  height: number;
};

export type WebCropOverlayProps = {
  imageUri: string;
  naturalWidth: number;
  naturalHeight: number;
  onConfirm: (region: CropRegion) => void;
  onCancel: () => void;
};

// Maximum display dimension (px). The image is scaled to fit within this box.
const MAX_DISPLAY_PX = 560;
// Minimum crop dimension in display pixels (prevents zero-size crops)
const MIN_CROP_PX = 20;
// Corner handle size (px)
const HANDLE_PX = 16;

type Rect = { x: number; y: number; w: number; h: number };
type DragMode = 'move' | 'tl' | 'tr' | 'bl' | 'br' | null;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const WebCropOverlay: React.FC<WebCropOverlayProps> = ({
  imageUri,
  naturalWidth,
  naturalHeight,
  onConfirm,
  onCancel,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  // Display dimensions after scaling to fit MAX_DISPLAY_PX
  const [displayW, setDisplayW] = useState(0);
  const [displayH, setDisplayH] = useState(0);
  // Current crop rect in display coordinates
  const [crop, setCrop] = useState<Rect>({ x: 0, y: 0, w: 0, h: 0 });

  const dragMode = useRef<DragMode>(null);
  const dragOrigin = useRef({ mx: 0, my: 0, x: 0, y: 0, w: 0, h: 0 });

  // Set initial display size and crop once we know natural image dimensions
  useEffect(() => {
    const nw = naturalWidth || 1;
    const nh = naturalHeight || 1;
    const scale = Math.min(MAX_DISPLAY_PX / nw, MAX_DISPLAY_PX / nh, 1);
    const dw = Math.round(nw * scale);
    const dh = Math.round(nh * scale);
    setDisplayW(dw);
    setDisplayH(dh);
    setCrop({ x: 0, y: 0, w: dw, h: dh });
  }, [naturalWidth, naturalHeight]);

  const scaleToNatural = useCallback(
    (rect: Rect): CropRegion => {
      if (!displayW || !displayH) {
        return { originX: 0, originY: 0, width: naturalWidth, height: naturalHeight };
      }
      const sx = naturalWidth / displayW;
      const sy = naturalHeight / displayH;
      return {
        originX: Math.round(clamp(rect.x * sx, 0, naturalWidth)),
        originY: Math.round(clamp(rect.y * sy, 0, naturalHeight)),
        width: Math.round(clamp(rect.w * sx, 1, naturalWidth)),
        height: Math.round(clamp(rect.h * sy, 1, naturalHeight)),
      };
    },
    [displayW, displayH, naturalWidth, naturalHeight]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, mode: DragMode) => {
      e.preventDefault();
      e.stopPropagation();
      dragMode.current = mode;
      dragOrigin.current = { mx: e.clientX, my: e.clientY, ...crop };
    },
    [crop]
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragMode.current) return;
    const dx = e.clientX - dragOrigin.current.mx;
    const dy = e.clientY - dragOrigin.current.my;
    const { x: ox, y: oy, w: ow, h: oh } = dragOrigin.current;

    setCrop((prev) => {
      let { x, y, w, h } = prev;
      switch (dragMode.current) {
        case 'move':
          x = clamp(ox + dx, 0, displayW - ow);
          y = clamp(oy + dy, 0, displayH - oh);
          w = ow;
          h = oh;
          break;
        case 'br':
          w = clamp(ow + dx, MIN_CROP_PX, displayW - ox);
          h = clamp(oh + dy, MIN_CROP_PX, displayH - oy);
          x = ox;
          y = oy;
          break;
        case 'tl': {
          const nx = clamp(ox + dx, 0, ox + ow - MIN_CROP_PX);
          const ny = clamp(oy + dy, 0, oy + oh - MIN_CROP_PX);
          w = ow - (nx - ox);
          h = oh - (ny - oy);
          x = nx;
          y = ny;
          break;
        }
        case 'tr': {
          const ny = clamp(oy + dy, 0, oy + oh - MIN_CROP_PX);
          w = clamp(ow + dx, MIN_CROP_PX, displayW - ox);
          h = oh - (ny - oy);
          x = ox;
          y = ny;
          break;
        }
        case 'bl': {
          const nx = clamp(ox + dx, 0, ox + ow - MIN_CROP_PX);
          w = ow - (nx - ox);
          h = clamp(oh + dy, MIN_CROP_PX, displayH - oy);
          x = nx;
          y = oy;
          break;
        }
        default:
          break;
      }
      return { x, y, w, h };
    });
  }, [displayW, displayH]);

  const handleMouseUp = useCallback(() => {
    dragMode.current = null;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleConfirm = () => {
    onConfirm(scaleToNatural(crop));
  };

  const cornerStyle = (cursor: string): React.CSSProperties => ({
    position: 'absolute',
    width: HANDLE_PX,
    height: HANDLE_PX,
    background: 'white',
    border: '2px solid rgba(0,0,0,0.5)',
    cursor,
  });

  return (
    <div
      data-testid="web-crop-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        userSelect: 'none',
      }}
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 12 }}>
        Drag to reposition · Drag corners to resize
      </p>

      {/* Image + crop rectangle */}
      <div style={{ position: 'relative', width: displayW, height: displayH }}>
        {/* Darkened outer areas */}
        <img
          ref={imgRef}
          src={imageUri}
          alt="Crop preview"
          style={{
            display: 'block',
            width: displayW,
            height: displayH,
            objectFit: 'cover',
            opacity: 0.4,
          }}
          draggable={false}
        />
        {/* Bright crop window */}
        <div
          style={{
            position: 'absolute',
            left: crop.x,
            top: crop.y,
            width: crop.w,
            height: crop.h,
            overflow: 'hidden',
            cursor: 'move',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'move')}
        >
          <img
            src={imageUri}
            alt=""
            style={{
              position: 'absolute',
              left: -crop.x,
              top: -crop.y,
              width: displayW,
              height: displayH,
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
            draggable={false}
          />
          {/* Crop border */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              border: '2px solid white',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Corner handles */}
        <div
          style={{ ...cornerStyle('nw-resize'), left: crop.x - HANDLE_PX / 2, top: crop.y - HANDLE_PX / 2 }}
          onMouseDown={(e) => handleMouseDown(e, 'tl')}
        />
        <div
          style={{ ...cornerStyle('ne-resize'), left: crop.x + crop.w - HANDLE_PX / 2, top: crop.y - HANDLE_PX / 2 }}
          onMouseDown={(e) => handleMouseDown(e, 'tr')}
        />
        <div
          style={{ ...cornerStyle('sw-resize'), left: crop.x - HANDLE_PX / 2, top: crop.y + crop.h - HANDLE_PX / 2 }}
          onMouseDown={(e) => handleMouseDown(e, 'bl')}
        />
        <div
          style={{ ...cornerStyle('se-resize'), left: crop.x + crop.w - HANDLE_PX / 2, top: crop.y + crop.h - HANDLE_PX / 2 }}
          onMouseDown={(e) => handleMouseDown(e, 'br')}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button
          data-testid="web-crop-cancel"
          onClick={onCancel}
          style={{
            padding: '9px 28px',
            background: 'transparent',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Cancel
        </button>
        <button
          data-testid="web-crop-confirm"
          onClick={handleConfirm}
          style={{
            padding: '9px 28px',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Crop
        </button>
      </div>
    </div>
  );
};

export default WebCropOverlay;
